<template>
  <div class="flex flex-col flex-auto bg-primary">
    <div v-if="image?.file?.url" class="flex">
      <div :class="{ 'lg:w-1/2': altImage?.file?.url }" class="w-full">
        <UiLazyImg
          class="aspect-[375/430] w-full"
          :class="
            altImage?.file?.url ? 'lg:aspect-[720/650]' : 'lg:aspect-[1440/650]'
          "
          :alt="
            image?.alt ||
            blogData?.data?.currentTranslation?.title ||
            $t('image')
          "
          :src="image?.file?.url"
          cover
        />
      </div>
      <div v-if="altImage?.file?.url" class="w-1/2 max-lg:hidden">
        <UiLazyImg
          class="aspect-[720/650] w-full"
          :alt="
            altImage?.alt ||
            blogData?.data?.currentTranslation?.title ||
            $t('image')
          "
          :src="altImage?.file?.url"
          cover
        />
      </div>
    </div>

    <div class="container">
      <div class="py-4">
        <nuxt-link
          class="ui-text-xs text-white font-medium uppercase inline-flex items-center"
          :to="localePath('/blog')"
        >
          <UiIcon name="arrow_left_long" class="size-4.5 mr-1" />
          {{ $t("back") }}
        </nuxt-link>
      </div>

      <div class="pb-8">
        <h1 class="text-white text-center">
          {{ blogData?.data?.currentTranslation?.title }}
        </h1>
        <div class="ui-text-m text-white text-center mt-4">
          {{ publishedAt }}
        </div>
      </div>

      <div class="pb-8">
        <template v-if="mappedPostEls?.length">
          <template v-for="(el, idx) in mappedPostEls" :key="idx">
            <div
              v-if="!el.dataset?.['data-insert']"
              class="ui-text-m text-white"
              v-html="el.outerHTML"
            />
            <div v-else :id="el.dataset?.id" class="ui-text-m text-white" />
          </template>
        </template>
        <div
          v-else
          v-html="blogData?.data?.currentTranslation?.description"
          class="ui-text-m text-white"
        />
      </div>

      <template v-for="(component, idx) in portalArray">
        <teleport
          v-if="component.data && component.moduleType"
          :key="idx"
          :to="'#' + component.componentName + '-id-' + idx"
        >
          <component
            :is="getComponent(component.componentName, component.moduleType)"
            :name="component.moduleType"
            :block-name="component.componentName"
            :data="component.data"
          ></component>
        </teleport>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { parse, type Node } from "node-html-parser";
import { format } from "date-fns";
import { useI18n } from "vue-i18n";

const i18nHead = useLocaleHead({
  addSeoAttributes: true,
});
const { setBlogPostPageLdSchema } = useJsonLdSchema();

interface ImappedPostEls {
  outerHTML: string;
  dataset: { [key: string]: string };
  id: string;
}
interface IportalArray {
  componentName: string;
  uniqId: string;
  moduleType?: string;
  data?: any;
}

const config = useRuntimeConfig();
const generalStore = useGeneralStore();
const route = useRoute();
const { $api } = useNuxtApp();
const { t } = useI18n();
const { visitEvent } = useEvents();
const topElsArray = ref<Node[]>([]);
const mappedPostEls = ref<ImappedPostEls[]>([]);
const portalArray = ref<IportalArray[]>([]);
const localePath = useLocalePath();

const { getComponent } = useBlockContainer();

const slug = computed(() => route.params.slug);
const { data: blogData } = await useAsyncCachedData(
  `post-${slug.value as string}`,
  () => {
    return $api.blog.getPostBySlug(slug.value as string);
  },
);

const publishedAt = computed(() => {
  return blogData?.value?.data?.published_at
    ? format(
        new Date(blogData?.value?.data?.published_at),
        generalStore.getSystemDateFormat,
      )
    : "";
});

const image = computed(() =>
  getImageByPosition(blogData?.value?.data?.medias || [], "page"),
);

const altImage = computed(() =>
  getImageByPosition(blogData?.value?.data?.medias || [], "alt"),
);

const blogPostData = computed(() => blogData.value?.data);

if (blogPostData.value) {
  setBlogPostPageLdSchema({
    post: blogPostData,
    image: image?.value?.file?.url || "",
    name: blogPostData.value?.currentTranslation?.title || "",
  });
}

parseHtmlData(blogData.value?.data?.currentTranslation?.description || "");

const { seoTitle, seoDescription } = usePageSeo();

useHead({
  meta: [
    ...metaGenerator({
      title:
        blogData.value?.data?.currentTranslation?.meta_title || seoTitle.value,
      description:
        blogData.value?.data?.currentTranslation?.meta_description ||
        seoDescription.value,
      image:
        blogData.value?.data?.medias?.find((el) => el.position === "open-graph")
          ?.file?.url || "",
      keywords: blogData.value?.data?.currentTranslation?.meta_keywords || "",
      url:
        (generalStore.bootstrap?.settings?.front_domain ||
          config.public.domain) +
        "/blog/" +
        route.params.section +
        "/" +
        route.params.slug,
    }),
    ...(i18nHead.value.meta || []),
  ],
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang,
  },
  link: [...(i18nHead.value.link || [])],
});

useSeoMeta({
  title: () => seoTitle.value,
  ogTitle: () => seoTitle.value,
  description: () =>
    blogData.value?.data?.currentTranslation?.meta_description ||
    seoDescription.value,
  ogDescription: () =>
    blogData.value?.data?.currentTranslation?.meta_description ||
    seoDescription.value,
});

function usePageSeo() {
  const seoTitle = computed(() => {
    let title =
      blogData.value?.data?.currentTranslation?.meta_title ||
      blogData.value?.data?.currentTranslation?.title ||
      "";
    if (generalStore.settings?.company_name) {
      title = `${title} | ${generalStore.settings.company_name}`;
    }
    return title;
  });

  const seoDescription = computed(() => {
    const title = blogData.value?.data?.currentTranslation?.title || "";
    return `${title} ${t("pageMetaDescription")}`;
  });
  return {
    seoTitle,
    seoDescription,
  };
}

onMounted(async () => {
  visitEvent();

  portalArray.value = mappedPostEls.value.reduce((acc, el) => {
    if (el.dataset?.id) {
      acc.push({
        componentName: el.dataset?.["data-insert"] as string,
        uniqId: el.id || "",
      });
    }
    return acc;
  }, [] as IportalArray[]);

  const blockArray = await getComponents(portalArray.value);

  portalArray.value = portalArray.value.map((el) => {
    const component = blockArray.find(
      (block) => block.data.name === el.componentName,
    );
    return {
      ...el,
      data: component?.data.currentTranslation?.content,
      moduleType: component?.data.type.identifier,
    };
  });
});

function parseHtmlData(htmlString) {
  if (!htmlString) {
    return;
  }
  const serverParser = parse(htmlString);

  let activeIdx = 0;
  let idIdx = 0;

  const firstDivEl = parse("<div></div>");
  topElsArray.value.push(firstDivEl.childNodes[0]);

  serverParser.childNodes.forEach((el) => {
    if (!el?.attrs?.["data-insert"]) {
      if (topElsArray.value[activeIdx]?.appendChild) {
        topElsArray.value[activeIdx]?.appendChild(el);
      }
    } else {
      activeIdx = activeIdx + 2;
      const divEl = parse("<div></div>");
      el.setAttribute("id", el.attrs["data-insert"] + "-id-" + idIdx);
      idIdx = idIdx + 1;
      topElsArray.value.push(el);
      topElsArray.value.push(divEl);
    }
  });

  mappedPostEls.value = topElsArray.value.map((el) => {
    return {
      outerHTML: el?.outerHTML || "",
      dataset: { ...el.attrs },
      id: el.id,
    };
  });
}

async function getComponents(portalArrayData: IportalArray[]) {
  const componentNames = portalArrayData.map(
    (component) => component.componentName,
  );
  const uniqueComponentNames = [...new Set(componentNames)];

  const promises = uniqueComponentNames.map((name: string) => {
    return $api.blocks.getBlock(name);
  });

  return await Promise.all(promises);
}
</script>
